import * as Yup from "yup";

// ----------------------- //
//          UTILS          //
// ----------------------- //

function addRequiredCheck(scheme, name, messages, required) {
  if (required === true) {
    return scheme.required(messages.required.replace("*name", name));
  }
  return scheme;
}

function addRequiredWhenDefinedCheck(scheme, name, messages) {
  return Yup.lazy(value => {
    switch (typeof value) {
      case "undefined":
        return scheme;
      case "string":
        return addRequiredCheck(scheme, name, messages, true);
      default:
        throw new yup.ValidationError("Must be a string or `undefined`");
    }
  });
}

// ----------------------- //
//    REQUIRED BOOLEAN     //
// ----------------------- //

export function requiredBoolean(name, messages, required) {
  const base = Yup.boolean();
  return addRequiredCheck(base, name, messages, required);
}

// ----------------------- //
//     REQUIRED STRING     //
// ----------------------- //

export function requiredString(name, messages, required) {
  const base = Yup.string();
  return addRequiredCheck(base, name, messages, required);
}

// ----------------------- //
//      SIMPLE STRING      //
// ----------------------- //

export function simpleStringSchema(name, messages, required) {
  const base = Yup.string()
    .min(3, messages.too_short.replace("*num", "3"))
    .max(50, messages.too_long.replace("*num", "50"));

  return addRequiredCheck(base, name, messages, required);
}

export function simpleLongStringSchema(name, messages, required) {
  const base = Yup.string()
    .min(3, messages.too_short.replace("*num", "3"))
    .max(65535, messages.too_long.replace("*num", "65535")); // LONGTEXT in MySQL. FYI: the <input> element allows by default a maximum number of characters of 524288.

  return addRequiredCheck(base, name, messages, required);
}

export function requiredWhenDefinedStringSchema(name, messages) {
  const base = Yup.string()
    .min(3, messages.too_short.replace("*num", "3"))
    .max(50, messages.too_long.replace("*num", "50"));

  return addRequiredWhenDefinedCheck(base, name, messages);
}

// ----------------------- //
//     SIMPLE DROPDOWN     //
// ----------------------- //

export function dropdownSchema(name, messages, required = true) {
  const base = Yup.mixed();
  return addRequiredCheck(base, name, messages, required);
}

// ----------------------- //
//      SIMPLE SLIDER      //
// ----------------------- //

export function sliderSchema(name, messages) {
  return Yup.mixed().required(messages.required.replace("*name", name));
}

// ------------------------------ //
//     ONE OF POSSIBLE VALUES     //
// ------------------------------ //

export function oneOfPossibleValues(name, messages, possibleValues, required) {
  const base = Yup.mixed().oneOf(
    possibleValues,
    messages.one_of_possible_values.replace("*name", name)
  );
  return addRequiredCheck(base, name, messages, required);
}

// ----------------------- //
//         USERNAME        //
// ----------------------- //

const USERNAME_REG_EX = /^[a-zA-Z]([a-zA-Z0-9_.-]{1,23})([a-zA-Z0-9_-]{1})$/;

export function usernameSchema(name, messages, required) {
  const base = Yup.string()
    .min(3, messages.too_short.replace("*num", "3"))
    .max(20, messages.too_long.replace("*num", "20"))
    .matches(USERNAME_REG_EX, messages.username);

  return addRequiredCheck(base, name, messages, required);
}

// ----------------------- //
//         PASSWORD        //
// ----------------------- //

const PASSWORD_REG_EX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[ !"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]).*$/;

export function passwordSchema(name, messages, required) {
  const base = Yup.string()
    .min(8, messages.too_short.replace("*num", "8"))
    .max(25, messages.too_long.replace("*num", "25"))
    .matches(PASSWORD_REG_EX, messages.password);

  return addRequiredCheck(base, name, messages, required);
}

export function passwordConfirmationSchema(name, messages, required) {
  const base = Yup.string().oneOf(
    [Yup.ref("password"), null],
    messages.password_match
  );

  return addRequiredCheck(base, name, messages, required);
}

// ----------------------- //
//          EMAIL          //
// ----------------------- //

export function emailSchema(name, messages, required) {
  const base = Yup.string().email(messages.email);

  return addRequiredCheck(base, name, messages, required);
}

export function emailConfirmationSchema(name, messages, required) {
  const base = Yup.string().oneOf(
    [Yup.ref("email"), null],
    messages.email_match
  );

  return addRequiredCheck(base, name, messages, required);
}

// ----------------------- //
//     TIMEZONE OBJECT     //
// ----------------------- //

// Temp just to get things working needs building out

export function timezoneSchema(name, messages, required) {
  const base = Yup.object().shape({
    "@id": simpleStringSchema(name, messages, true)
  });

  return addRequiredCheck(base, name, messages, required);
}

// ----------------------- //
//      SIMPLE TIME        //
// ----------------------- //

// Temp just to get things working needs building out

export function simpleTimeSchema(name, messages, required) {
  const base = Yup.string()
    .min(8, messages.too_short.replace("*num", "8"))
    .max(8, messages.too_long.replace("*num", "8"));

  return addRequiredCheck(base, name, messages, required);
}

// --------------------------------- //
//     SSH PUBLIC KEY VALIDATION     //
// --------------------------------- //

const SSH_PUBLIC_KEY_REG_EX =
  /^ssh-rsa AAAA[a-zA-Z0-9+\/]+[=]{0,3}( [^@]+@[^@]+)?$/;

export function sshPubliclKeySchema(name, messages, required) {
  const base = Yup.string().matches(SSH_PUBLIC_KEY_REG_EX, messages.ssh_key);
  return addRequiredCheck(base, name, messages, required);
}

// ----------------------- //
//     CIDR VALIDATION     //
// ----------------------- //

// Thanks Aurelio!
Yup.addMethod(
  Yup.string,
  "cidr",
  function (message, allowPrivate, allowPublic) {
    const ip4ToInt = ip => {
      const value = ip
        .split(".")
        .reduce((int, oct) => (int << 8) + parseInt(oct, 10), 0);

      return value >>> 0;
    };

    const dec2bin = dec => {
      if (!dec) return (0).toString(2);
      return (dec >>> 0).toString(2);
    };

    return this.test("cidr", message, function (value) {
      const { path, createError } = this;

      if (!value) {
        // we're dealing with an empty value, do not throw a CIDR error here: leave the required (true|false) for another function
        return true;
      }

      const regexpatt =
        /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(?:\/(?:[0-9]|[1-2][0-9]|3[0-2]))?$/;
      const passedregex = regexpatt.test(value);
      const regexprivatepatt =
        /(^127\.)|(^10\.)|(^172\.1[6-9]\.)|(^172\.2[0-9]\.)|(^172\.3[0-1]\.)|(^192\.168\.)/;
      const isprivateregex = regexprivatepatt.test(value);

      if (
        !passedregex ||
        (isprivateregex && !allowPrivate) ||
        (isprivateregex && allowPublic)
      ) {
        return createError({ path, message });
      }

      const [ipstring, bits = -1] = value.split("/");

      if (bits === -1) {
        return createError({ path, message });
      }

      // console.log(ipstring, bits);
      const maskint = ~(2 ** (32 - bits) - 1);
      const ipint = ip4ToInt(ipstring);
      const ipbinary = dec2bin(ipint);
      // console.log(ipbinary, "(ip - " + ipstring + ")");
      // console.log(maskbinary, "(mask - " + bits + ")");
      const ipANDmask = dec2bin(ipint & maskint);
      // console.log(ipANDmask, "(ip AND mask)");

      return ipbinary == ipANDmask || createError({ path, message });
    });
  }
);

export function cidrSchema(
  name,
  messages,
  required,
  allowPrivate = false,
  allowPublic = true
) {
  let cidrMessages = messages.cidr;
  if (allowPrivate && !allowPublic) cidrMessages = messages.cidr_private;
  if (!allowPrivate && allowPublic) cidrMessages = messages.cidr_public;

  const base = Yup.string().cidr(cidrMessages, allowPrivate, allowPublic);

  return addRequiredCheck(base, name, messages, required);
}

// --------------------- //
//     IP VALIDATION     //
// --------------------- //

Yup.addMethod(Yup.string, "ip", function (message, allowPrivate, allowPublic) {
  return this.test("ip", message, function (value) {
    const { path, createError } = this;

    if (!value) return createError({ path, message });

    const regexpatt =
      /^^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/;
    const passedregex = regexpatt.test(value);
    const regexprivatepatt =
      /(^127\.)|(^10\.)|(^172\.1[6-9]\.)|(^172\.2[0-9]\.)|(^172\.3[0-1]\.)|(^192\.168\.)/;
    const isprivateregex = regexprivatepatt.test(value);

    if (
      !passedregex ||
      (isprivateregex && !allowPrivate) ||
      (isprivateregex && allowPublic)
    ) {
      return createError({ path, message });
    }

    return passedregex;
  });
});

export function ipSchema(
  name,
  messages,
  required,
  allowPrivate = false,
  allowPublic = true
) {
  let ipMessages = messages.ip;
  if (allowPrivate && !allowPublic) ipMessages = messages.ip_private;
  if (!allowPrivate && allowPublic) ipMessages = messages.ip_public;

  const base = Yup.string().ip(ipMessages, allowPrivate, allowPublic);

  return addRequiredCheck(base, name, messages, required);
}

// --------------------------------- //
//     SECURITY GROUP VALIDATION     //
// --------------------------------- //

Yup.addMethod(Yup.string, "securityGroup", function (message) {
  return this.test("securityGroup", message, function (value) {
    const regexpatt = /^([0-9]*\/)?sg-([a-zA-Z0-9]*)/;
    const passedregex = regexpatt.test(value);
    return passedregex;
  });
});

export function securityGroupSchema(name, messages, required) {
  const base = Yup.string().securityGroup(messages.cidr_public);
  return addRequiredCheck(base, name, messages.cidr_public, required);
}

// ----------------------------------------- //
//     CIDR OR SECURITY GROUP VALIDATION     //
// ----------------------------------------- //

export function cidrOrSecurityGroupSchema(
  name,
  messages,
  required,
  allowPrivate = false,
  allowPublic = true
) {
  let cidrMessages = messages.cidr;
  if (allowPrivate && !allowPublic) cidrMessages = messages.cidr_private;
  if (!allowPrivate && allowPublic) cidrMessages = messages.cidr_public;

  return Yup.lazy(value => {
    let base;

    // a nasty pre-check but struggling to find a nice way to run either/or on schemas
    if (typeof value === "string" && value.includes("sg-")) {
      base = Yup.string().securityGroup(cidrMessages);
    } else {
      base = Yup.string().cidr(cidrMessages, allowPrivate, allowPublic);
    }

    return addRequiredCheck(base, name, messages, required);
  });
}

// -------------------- //
// -- DEFINED METHOD -- //
// -------------------- //

// can be used with arrays
Yup.addMethod(Yup.mixed, "defined", function () {
  return this.test(
    "defined",
    "{path} must be defined",
    value => value !== undefined
  );
});

// ------------------------------ //
// -- CIDR WORKING TEST VALUES -- //
// ------------------------------ //

// Example security group: sg-0eb9345245bf872ff

// console.log("Valid CIDR? -", validIP4CIDR("192.168.1.0/24"), "\n");
// console.log("Valid CIDR? -", validIP4CIDR("192.168.1.1/24"), "\n");

// console.log("Valid CIDR? -", validIP4CIDR("69.171.240.0/20"), "\n");
// console.log("Valid CIDR? -", validIP4CIDR("69.171.240.12/20"), "\n");

// console.log("Valid CIDR? -", validIP4CIDR("74.125.38.0/23"), "\n");
// console.log("Valid CIDR? -", validIP4CIDR("74.125.38.0/23"), "\n");

// console.log("Valid CIDR? -", validIP4CIDR("10.0.0.0/8"), "\n");
// console.log("Valid CIDR? -", validIP4CIDR("10.10.10.8/8"), "\n");

// console.log("Valid CIDR? -", validIP4CIDR("0.0.0.0/0"), "\n");
// console.log("Valid CIDR? -", validIP4CIDR("0.0.1.0/0"), "\n");

// console.log("Valid CIDR? -", validIP4CIDR("225.34.8.0/21"), "\n");
// console.log("Valid CIDR? -", validIP4CIDR("225.34.3.2/21"), "\n");

// console.log("Valid CIDR? -", validIP4CIDR("225.34.25.2/31"), "\n");
// console.log("Valid CIDR? -", validIP4CIDR("225.34.25.1/31"), "\n");

// console.log("Valid CIDR? -", validIP4CIDR("128.256.3.0/33"), "\n");
// console.log("Valid CIDR? -", validIP4CIDR("128.255.3.0/32"), "\n");

// console.log("Valid CIDR? -", validIP4CIDR("128.10.3./32"), "\n");
// console.log("Valid CIDR? -", validIP4CIDR("128.10.3.0/32"), "\n");
